import { Configuration } from "Models/configuration";
import { Node } from "Models/node";
import { Partition, PartitionType } from "Models/partition";
import {
    setConsumers,
    setNodes,
    setPartitions,
    updatePartition,
} from "Redux/actions";
import { getConfig, getNodes } from "Redux/selectors";
import store, { dispatch } from "Redux/store";
import { getAllConsumers, setupConsumers } from "./consumers.service";
import {
    getActiveReplicasForLead,
    getActiveLeads,
    getAllPartitions,
    getLeads,
    getReplicas,
    syncReplicas,
} from "./partitions.service";

export const getAllNodes = (): Node[] => getNodes(store.getState());

export const getActiveNodes = (nodes: Node[]): Node[] =>
    nodes.filter((n) => n.active);

export const getNodePartitions = (node: Node) =>
    getAllPartitions()?.filter((p) => p.nodeId === node.id) ?? [];

export const getNodeLeads = (node: Node) => getLeads(getNodePartitions(node));

export const getNodeReplicas = (node: Node) =>
    getReplicas(getNodePartitions(node));

export const getNodePartitionsLength = (node: Node) =>
    getNodePartitions(node).length ?? 0;

export const setupNodes = (
    { nodes }: Partial<Configuration>,
    existingNodes: Node[]
) =>
    Array.from({ length: nodes ?? 0 }, (_, i) =>
        existingNodes[i] && !existingNodes[i].active
            ? existingNodes[i]
            : new Node(i + 1)
    );

export const toggleNode = (node: Node) => {
    const active = !node.active;
    const all = getAllNodes().map((n) =>
        n.id === node.id ? { ...n, active } : n
    );
    const allPartitions = getAllPartitions().map((p) =>
        p.nodeId === node.id ? { ...p, active } : p
    );
    dispatch(setNodes(all));
    dispatch(setPartitions(allPartitions));
    if (!active) deactivateNode(node);
    else activateNode(node);
};

const activateNode = (node: Node) => {
    // switch leads to replicas or reactivate
    getNodePartitions(node).forEach((p) => {
        const leadExists = getActiveLeads(getAllPartitions()).find(
            (active) => active.id === p.id && active.nodeId !== node.id
        );
        dispatch(
            updatePartition({
                ...p,
                type: leadExists ? PartitionType.REPLICA : PartitionType.LEAD,
            })
        );
    });

    syncReplicas(getAllPartitions());
    const consumers = setupConsumers(
        getConfig(store.getState())!,
        getAllConsumers(),
        getAllPartitions()
    );
    dispatch(setConsumers(consumers ?? []));
};

const deactivateNode = (node: Node) => {
    getNodeLeads(node).forEach((l) => {
        switchLead(l);
    });
    const consumers = setupConsumers(
        getConfig(store.getState())!,
        getAllConsumers(),
        getAllPartitions()
    );
    dispatch(setConsumers(consumers ?? []));
};

const switchLead = (lead: Partition) => {
    const newLead = getActiveReplicasForLead(lead, getAllPartitions()).shift();
    if (newLead) {
        dispatch(
            updatePartition({
                ...newLead,
                type: PartitionType.LEAD,
            })
        );
    }
};
